import React from 'react';
import { DotProps } from 'recharts';
import { token } from '@atlaskit/tokens';
import { css } from '@emotion/core';

import { DataPointWithEvents } from '@/ui';
import {
  BREACH_COLOR,
  BREACH_LIGHT_COLOR,
  BURN_LIGHT_COLOR,
  BURN_COLOR,
  colors,
  INVERSE_COLOR,
} from '@/styles';
import { getEventTypes } from '@/components/composed/slo-card/utils';

import DotCircle from './dot-circle';
import { DOT_RADIUS } from './constants';

type CustomDotProps = DotProps & { payload?: DataPointWithEvents };

const incidentIconStyles = css({
  fontSize: DOT_RADIUS * 2,
  textAnchor: 'middle',
});

const Dot: React.FC<CustomDotProps> = props => {
  /* eslint-disable react/destructuring-assignment */
  const cx = props.cx!;
  const cy = props.cy!;
  const payload = props.payload!;

  let offsetY = 0;

  if (payload.result && (payload.events.length || payload.incidents.length)) {
    const {
      breachEvents,
      burnEvents,
      sfxBreachEvents,
      sfxBurnEvents,
      lowTrafficBreachEvents,
      lowTrafficBurnEvents,
      lowTrafficSFxBreachEvents,
      lowTrafficSFxBurnEvents,
    } = getEventTypes(payload.events);

    const breachCount = breachEvents.length + sfxBreachEvents.length;
    const lowTrafficBreachCount = lowTrafficBreachEvents.length + lowTrafficSFxBreachEvents.length;
    const burnCount = burnEvents.length + sfxBurnEvents.length;
    const lowTrafficBurnCount = lowTrafficBurnEvents.length + lowTrafficSFxBurnEvents.length;

    /* eslint-disable-next-line no-return-assign */
    return (
      <g>
        <circle
          cx={cx}
          cy={cy}
          r={4}
          fill={token('color.text', colors.N800)}
          stroke={token('elevation.surface', '#FFF')}
          strokeWidth={2}
        />
        {payload.incidents.length > 0 && (
          <g>
            <text x={cx} y={cy - 10} css={incidentIconStyles}>
              {String.fromCodePoint(0x1f525)}
            </text>
          </g>
        )}
        {breachCount > 0 && (
          <DotCircle
            dotX={cx - DOT_RADIUS + 1}
            dotY={cy + (offsetY += DOT_RADIUS)}
            fill={BREACH_COLOR}
            stroke={BREACH_COLOR}
            textX={cx}
            textY={cy + (offsetY += DOT_RADIUS - 1 + DOT_RADIUS * 0.38)}
            textFill={INVERSE_COLOR}
            count={breachCount}
          />
        )}
        {lowTrafficBreachCount > 0 && (
          <DotCircle
            dotX={cx - DOT_RADIUS + 1}
            dotY={cy + (offsetY += DOT_RADIUS)}
            fill={BREACH_LIGHT_COLOR}
            stroke={BREACH_COLOR}
            textX={cx}
            textY={cy + (offsetY += DOT_RADIUS - 1 + DOT_RADIUS * 0.38)}
            textFill={BREACH_COLOR}
            count={lowTrafficBreachCount}
          />
        )}
        {burnCount > 0 && (
          <DotCircle
            dotX={cx - DOT_RADIUS + 1}
            dotY={cy + (offsetY += DOT_RADIUS)}
            fill={BURN_COLOR}
            stroke={BURN_COLOR}
            textX={cx}
            textY={cy + (offsetY += DOT_RADIUS - 1 + DOT_RADIUS * 0.38)}
            textFill={INVERSE_COLOR}
            count={burnCount}
          />
        )}
        {lowTrafficBurnCount > 0 && (
          <DotCircle
            dotX={cx - DOT_RADIUS + 1}
            dotY={cy + (offsetY += DOT_RADIUS)}
            fill={BURN_LIGHT_COLOR}
            stroke={BURN_COLOR}
            textX={cx}
            textY={cy + (offsetY += DOT_RADIUS - 1 + DOT_RADIUS * 0.38)}
            textFill={BURN_COLOR}
            count={lowTrafficBurnCount}
          />
        )}
      </g>
    );
  }

  return null;
};

export default Dot;
